/**
 * Compatibility Store (Zustand)
 * Manages progressive enrichment state, auto-recomputes on layer changes.
 * Persists layer data to user_enrichment_layers via Supabase.
 * Listens for canonical personality changes to auto-update Person A.
 */

import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import type {
  PersonProfile,
  ProgressiveRelationshipContext,
  CompatibilityResult,
  ConfidenceReport,
  DataLayer,
  DataLayerStatus,
  BayesianUpdate,
} from '@/types/compatibility';
import { computeCompatibility } from '@/lib/analysis/index';
import { computeConfidence } from '@/lib/analysis/confidence';

interface CompatibilityState {
  // State
  personA: PersonProfile | null;
  personB: PersonProfile | null;
  context: ProgressiveRelationshipContext | null;
  result: CompatibilityResult | null;
  confidence: ConfidenceReport | null;
  lastUpdate: BayesianUpdate | null;

  // Actions
  setPersonA: (p: PersonProfile) => void;
  setPersonB: (p: PersonProfile) => void;
  setContext: (ctx: ProgressiveRelationshipContext) => void;
  updatePersonLayer: (person: 'A' | 'B', layer: DataLayer, data: unknown) => void;
  recompute: () => void;
  reset: () => void;

  // Computed
  completionPercent: number;
  layerStatuses: DataLayerStatus[];
}

export const useCompatibilityStore = create<CompatibilityState>((set, get) => ({
  personA: null,
  personB: null,
  context: null,
  result: null,
  confidence: null,
  lastUpdate: null,
  completionPercent: 0,
  layerStatuses: [],

  setPersonA: (p) => {
    set({ personA: p });
    get().recompute();
  },

  setPersonB: (p) => {
    set({ personB: p });
    get().recompute();
  },

  setContext: (ctx) => {
    set({ context: ctx });
    get().recompute();
  },

  updatePersonLayer: (person, layer, data) => {
    const state = get();
    const target = person === 'A' ? state.personA : state.personB;
    if (!target) return;

    const layerMap: Record<string, string> = {
      demographics: 'demographics',
      attachment: 'attachment',
      values: 'values',
      loveLanguages: 'loveLanguages',
      lifestyle: 'lifestyle',
      narrative: 'narrative',
    };

    const field = layerMap[layer];
    if (!field) return;

    const priorResult = state.result;
    const priorScore = priorResult?.score || { value: 50, lower: 30, upper: 70, confidence: 'low' as const };

    const updated = { ...target, [field]: data };
    if (person === 'A') {
      set({ personA: updated });
      // Persist to DB (fire-and-forget for UX)
      supabase.auth.getUser().then(({ data: authData }) => {
        if (authData?.user?.id) {
          supabase.from('user_enrichment_layers').upsert([{
            user_id: authData.user.id,
            layer: layer as any,
            data: data as any,
            status: 'complete' as any,
            version: 1,
          }], { onConflict: 'user_id,layer' }).then(() => {});
        }
      });
    } else {
      set({ personB: updated });
    }

    // Recompute and generate Bayesian update
    const newState = get();
    if (newState.personA && newState.personB && newState.context) {
      const newResult = computeCompatibility(newState.personA, newState.personB, newState.context);
      const newConfidence = computeConfidence(newState.personA, newState.personB, newState.context);
      const completionPercent = newConfidence.numericConfidence * 100;

      // Identify new and updated claims
      const priorClaimIds = new Set([
        ...(priorResult?.synergies.map(c => c.id) || []),
        ...(priorResult?.frictions.map(c => c.id) || []),
        ...(priorResult?.growthEdges.map(c => c.id) || []),
      ]);
      const allNewClaims = [...newResult.synergies, ...newResult.frictions, ...newResult.growthEdges];
      const newClaims = allNewClaims.filter(c => !priorClaimIds.has(c.id));
      const updatedClaims = allNewClaims.filter(c => priorClaimIds.has(c.id));

      const bayesianUpdate: BayesianUpdate = {
        layer,
        priorScore,
        posteriorScore: newResult.score,
        newClaims,
        updatedClaims,
        explanation: newResult.score
          ? `Adding ${layer} data ${newResult.score.value > (priorScore?.value ?? 50) ? 'improved' : 'adjusted'} the compatibility estimate from ${priorScore?.value ?? '—'} to ${newResult.score.value} and narrowed the confidence interval.`
          : `Adding ${layer} data isn't enough for a score yet — no framework has data on both sides.`,
      };

      set({
        result: newResult,
        confidence: newConfidence,
        completionPercent,
        layerStatuses: newConfidence.layerStatuses,
        lastUpdate: bayesianUpdate,
      });
    }
  },

  recompute: () => {
    const { personA, personB, context } = get();
    if (!personA || !personB || !context) return;

    const result = computeCompatibility(personA, personB, context);
    const confidence = computeConfidence(personA, personB, context);

    set({
      result,
      confidence,
      completionPercent: confidence.numericConfidence * 100,
      layerStatuses: confidence.layerStatuses,
    });
  },

  reset: () => {
    set({
      personA: null,
      personB: null,
      context: null,
      result: null,
      confidence: null,
      lastUpdate: null,
      completionPercent: 0,
      layerStatuses: [],
    });
  },
}));

// Listen for canonical personality changes and update Person A
if (typeof window !== 'undefined') {
  window.addEventListener('personality-data-changed', ((event: CustomEvent) => {
    const store = useCompatibilityStore.getState();
    const personA = store.personA;
    if (!personA) return;

    const detail = event.detail || {};
    const changedFields = detail.changedFields as string[] | undefined;
    const newValues = detail.newValues as Record<string, unknown> | undefined;

    if (!changedFields || !newValues) return;

    let updated = { ...personA };
    for (const field of changedFields) {
      if (field === 'mbti' && typeof newValues.mbti === 'string') {
        updated = { ...updated, mbti: newValues.mbti };
      }
      if (field === 'big_five' && typeof newValues.big_five === 'object') {
        const bf = newValues.big_five as Record<string, number>;
        updated = { ...updated, bigFive: {
          O: bf.openness ?? bf.O ?? 50,
          C: bf.conscientiousness ?? bf.C ?? 50,
          E: bf.extraversion ?? bf.E ?? 50,
          A: bf.agreeableness ?? bf.A ?? 50,
          N: bf.neuroticism ?? bf.N ?? 50,
        }};
      }
      if (field === 'attachment' && typeof newValues.attachment === 'string') {
        // Map style name to approximate ECR-R scores
        const styleScores: Record<string, { anxiety: number; avoidance: number; primaryStyle: string }> = {
          secure: { anxiety: 2, avoidance: 2, primaryStyle: 'secure' },
          anxious: { anxiety: 5.5, avoidance: 2, primaryStyle: 'anxious' },
          avoidant: { anxiety: 2, avoidance: 5.5, primaryStyle: 'avoidant' },
          'fearful-avoidant': { anxiety: 5, avoidance: 5, primaryStyle: 'fearfulAvoidant' },
        };
        const scores = styleScores[newValues.attachment as string] || { anxiety: 3.5, avoidance: 3.5, primaryStyle: 'secure' };
        updated = { ...updated, attachment: scores as any };
      }
    }

    useCompatibilityStore.setState({ personA: updated });
    store.recompute();
  }) as EventListener);
}
